import { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Circles } from "react-loader-spinner";
import AccountPageScroll from "./components/AccountPageScroll";
import AccountPageHeader from "./components/AccountPageHeader";
import AccountTabsNav from "./components/AccountTabsNav";
import { ACCOUNT_TABS, getAccountTabFromPathname } from "./accountConfig";
import { useMyAccountData } from "./useMyAccountData";
import type { MyAccountOutletContext } from "./myAccountOutletContext";

export default function MyAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    clinic,
    payment,
    storage,
    storageError,
    storageLoading,
    employee,
    userLoading,
    loading,
    error,
    loadStorage,
    loadClinic,
    smsBalance,
    smsUsage,
    smsLoading,
    smsError,
    loadSms,
    logoUrl,
    userName,
    userRole,
    clinicNameToken,
    clinicPhone,
    userAddress,
  } = useMyAccountData();

  const activeTab = useMemo(
    () => getAccountTabFromPathname(location.pathname),
    [location.pathname]
  );

  const activeTabMeta = useMemo(
    () => ACCOUNT_TABS.find((t) => t.id === activeTab),
    [activeTab]
  );

  useEffect(() => {
    if (activeTab === "sms") {
      loadSms();
    }
  }, [activeTab, loadSms]);

  const outletContext = useMemo<MyAccountOutletContext>(
    () => ({
      navigate,
      clinic,
      clinicNameToken,
      userAddress,
      clinicPhone,
      logoUrl,
      employee,
      userLoading,
      userName,
      userRole,
      payment,
      storage,
      storageLoading,
      storageError,
      loadStorage,
      loadClinic,
      smsBalance,
      smsUsage,
      smsLoading,
      smsError,
      loadSms,
    }),
    [
      navigate,
      clinic,
      clinicNameToken,
      userAddress,
      clinicPhone,
      logoUrl,
      employee,
      userLoading,
      userName,
      userRole,
      payment,
      storage,
      storageLoading,
      storageError,
      loadStorage,
      loadClinic,
      smsBalance,
      smsUsage,
      smsLoading,
      smsError,
      loadSms,
    ]
  );

  if (loading) {
    return (
      <AccountPageScroll>
        <div className="flex min-h-full w-full flex-col items-center justify-center gap-4 py-16">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-primary/10 blur-xl scale-150" aria-hidden />
            <Circles height="56" width="56" color="#174d7a" ariaLabel="loading" />
          </div>
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Loading your account…
          </p>
        </div>
      </AccountPageScroll>
    );
  }

  return (
    <AccountPageScroll>
      <div className="flex w-full min-h-full flex-col">
        <AccountPageHeader
          logoUrl={logoUrl}
          clinicName={clinic?.clinicName || clinicNameToken}
          clinicId={clinic?.clinicId}
          userName={userName}
          userRole={userRole}
          onGoToDashboard={() => navigate("/client/dashboard")}
        />

        {error && (
          <div
            className="mx-3 mt-2 rounded-lg border border-amber-200/80 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200 sm:mx-4 xl:mx-5"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex flex-col xl:flex-row xl:items-start">
          <aside
            className="sticky top-0 z-10 hidden w-60 shrink-0 flex-col self-start border-r border-slate-200/80 bg-slate-50/80 dark:border-slate-700/80 dark:bg-slate-900/40 xl:flex"
            aria-label="Account navigation"
          >
            <AccountTabsNav layout="sidebar" />
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="sticky top-0 z-10 shrink-0 border-b border-slate-200/80 bg-white dark:border-slate-700/80 dark:bg-slate-800/50 xl:hidden">
              <AccountTabsNav layout="bar" />
            </div>

            <div className="shrink-0 border-b border-slate-100 bg-white/90 px-3 py-2 dark:border-slate-700/80 dark:bg-slate-800/40 sm:px-4 xl:hidden">
              <h2 className="text-base font-bold text-slate-900 dark:text-white">
                {activeTabMeta?.label}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {activeTabMeta?.description}
              </p>
            </div>

            <main
              className="bg-slate-50/40 px-3 py-3 pb-6 dark:bg-slate-900/20 sm:px-4 sm:py-4 sm:pb-8 xl:px-5 xl:py-4"
              role="tabpanel"
            >
              <Outlet context={outletContext} />
            </main>
          </div>
        </div>
      </div>
    </AccountPageScroll>
  );
}
